/**
 * Copyright Higharc Inc. 2020
 */
import * as THREE from "three";
import type { AOPassBlurParams } from "./AOPass";
import { AOPass } from "./AOPass";
import { DepthLimitedBlurShader2 } from "./DepthLimitedBlurShader2";
import type { DepthNormalPass } from "./DepthNormalPass";
import type { DepthTextureType } from "./DepthTextureType";
import { GTAOShader } from "./GTAOShader";
import { screenSpaceViewPositionPrepareShader, screenSpaceViewNormalPrepareShader } from "./screenSpaceHelpersGLSL";
import { SSFXRendererOutput } from "./SSFXRendererOutput";

const tempQuaternion = new THREE.Quaternion();

// It doesn't necessarily make sense to use every mip level in the shader. This stride value controls how many depth mip levels are picked for the shader.
const DEPTH_MIP_STRIDE = 2;

export interface GTAOPassParams extends AOPassBlurParams {
  aoIntensity: number;
  worldSpaceScale: number;

  // This affects memory access locality which can have at least a small effect on performance.
  // A small value here can also prevent artifacts at screen edges as geometry comes into view.
  maxScreenSpaceKernelRadius: number;

  // Having smaller radiuses here prevents dark haloing around objects, but can cause overestimating visibility as well.
  // For orthographic projection, a larger radius is often useful to capture AO caused by rectangular prisms aligned with the camera,
  // like walls viewed from a top-down perspective.
  falloffMinRelativeRadius: number;
  falloffMaxRelativeRadius: number;

  // A value of 0.5 is usually a good tradeoff between quality and performance for realtime. 1.0 could be used for non-realtime rendering.
  aoResolutionMultiplier: number;

  // The total number of samples per pixel is numSamples * numAngleSamples.
  // Having a numAngleSamples value of 2 or 3 is good for reducing noise, but values above 2 hit diminishing returns fairly quickly.
  numSamples: number;
  numAngleSamples: number;

  blurNormalCutoffEnabled: boolean;
  depthCutoffBasedOnNormal: boolean;

  useNormalTextureInGTAO: boolean;

  // This parameter will lower GTAO intensity proportional to how vertical the surface is.
  // The range is from 0.0 to 1.0, and 1.0 means there's no AO at all on vertical surfaces.
  lowerIntensityOnVerticalSurfaces: number;

  // If cosine weighting is enabled, estimated visibility/AO is affected by foreshortening, i.e.
  // how much the average unoccluded direction differs from the surface normal.
  // In principle cosine weighting is more realistic, but leaving it disabled can be better for stylized rendering.
  // Also we don't compensate for bounce light yet anyway, so the added realism is questionable.
  cosineWeighting: boolean;
}

export const defaultGTAOPassParams: GTAOPassParams = {
  aoIntensity: 1.0,
  worldSpaceScale: 32.0,
  maxScreenSpaceKernelRadius: 0.1,
  falloffMaxRelativeRadius: 1.0,
  falloffMinRelativeRadius: 0.0,

  ssaoBlur: true,

  // These parameters have been optimized to be used together with TAA. Without TAA they're too low,
  // and blurKernelSamples=6 and blurStdDev=4 are suggested.
  blurKernelSamples: 3, // number of blur samples taken is double this value
  blurStdDev: 3,
  blurDepthCutoff: 4.0,
  blurNormalAngleCutoff: Math.PI * 0.3,

  lowerIntensityOnVerticalSurfaces: 0.3,

  aoResolutionMultiplier: 0.5,

  numSamples: GTAOShader.defines["NUM_AO_SAMPLES"],
  numAngleSamples: GTAOShader.defines["NUM_AO_ANGLE_SAMPLES"],

  useNormalTextureInGTAO: true,

  blurNormalCutoffEnabled: DepthLimitedBlurShader2.defines.NORMAL_CUTOFF === 1,
  depthCutoffBasedOnNormal: DepthLimitedBlurShader2.defines.DEPTH_CUTOFF_BASED_ON_NORMAL === 1,
  useNormalTextureInBlur: true,

  usePoissonBlur: false, // Note that this is set at initialization time depending on WebGL capabilities.

  cosineWeighting: false,

  output: SSFXRendererOutput.AO,
};

type MaterialSet = {
  gtaoMaterial: THREE.ShaderMaterial;
};

export class GTAOPass extends AOPass {
  readonly worldUpVector: THREE.Vector3;
  private depthTextureTypeInternal: DepthTextureType;
  get depthTextureType(): DepthTextureType {
    return this.depthTextureTypeInternal;
  }
  set depthTextureType(value: DepthTextureType) {
    if (value === this.depthTextureTypeInternal) {
      return;
    }
    this.depthTextureTypeInternal = value;
    this.refreshGTAOShaders();
  }

  private useNormalTextureInGTAO: boolean = defaultGTAOPassParams.useNormalTextureInGTAO;
  private useNormalTextureInBlur: boolean = defaultGTAOPassParams.useNormalTextureInBlur;
  private usePoissonBlur: boolean = defaultGTAOPassParams.usePoissonBlur;
  private cosineWeighting: boolean = defaultGTAOPassParams.cosineWeighting;

  private output: SSFXRendererOutput = defaultGTAOPassParams.output;

  private perspectiveMaterials: MaterialSet;
  private orthographicMaterials: MaterialSet;

  readonly params: GTAOPassParams;

  constructor(camera: THREE.Camera, depthNormalPass: DepthNormalPass, size?: THREE.Vector2) {
    super(camera, depthNormalPass, defaultGTAOPassParams.aoResolutionMultiplier, size);

    this.worldUpVector = new THREE.Vector3(0, 1, 0);
    this.depthTextureTypeInternal = depthNormalPass.depthTextureType;

    const scope = this; // eslint-disable-line @typescript-eslint/no-this-alias

    this.params = {
      ...defaultGTAOPassParams,

      set aoResolutionMultiplier(value) {
        scope.aoResolutionMultiplier = value;
        scope.refreshResolution();
      },
      get aoResolutionMultiplier() {
        return scope.aoResolutionMultiplier;
      },

      // Note that odd numSamples values work better than even ones.
      set numSamples(value: number) {
        if (value === scope.perspectiveMaterials.gtaoMaterial.defines["NUM_AO_SAMPLES"]) {
          return;
        }
        scope.perspectiveMaterials.gtaoMaterial.defines["NUM_AO_SAMPLES"] = value;
        scope.orthographicMaterials.gtaoMaterial.defines["NUM_AO_SAMPLES"] = value;
        scope.perspectiveMaterials.gtaoMaterial.needsUpdate = true;
        scope.orthographicMaterials.gtaoMaterial.needsUpdate = true;
      },
      get numSamples(): number {
        return scope.perspectiveMaterials.gtaoMaterial.defines["NUM_AO_SAMPLES"] as number;
      },

      // Note that odd numSamples values work better than even ones.
      set numAngleSamples(value: number) {
        if (value === scope.perspectiveMaterials.gtaoMaterial.defines["NUM_AO_ANGLE_SAMPLES"]) {
          return;
        }
        scope.perspectiveMaterials.gtaoMaterial.defines["NUM_AO_ANGLE_SAMPLES"] = value;
        scope.orthographicMaterials.gtaoMaterial.defines["NUM_AO_ANGLE_SAMPLES"] = value;
        scope.perspectiveMaterials.gtaoMaterial.needsUpdate = true;
        scope.orthographicMaterials.gtaoMaterial.needsUpdate = true;
      },
      get numAngleSamples(): number {
        return scope.perspectiveMaterials.gtaoMaterial.defines["NUM_AO_ANGLE_SAMPLES"] as number;
      },

      // This value toggles whether normal discontinuities trigger the blur cutoff.
      set blurNormalCutoffEnabled(value) {
        if (scope.perspectiveBlur.params.normalCutoffEnabled === value) {
          return;
        }
        scope.perspectiveBlur.params.normalCutoffEnabled = value;
        scope.orthographicBlur.params.normalCutoffEnabled = value;
        scope.perspectiveBlurNormalMap.params.normalCutoffEnabled = value;
        scope.orthographicBlurNormalMap.params.normalCutoffEnabled = value;
      },
      get blurNormalCutoffEnabled() {
        return scope.perspectiveBlur.params.normalCutoffEnabled;
      },

      // This value toggles whether normal information is taken advantage of when checking depth discontinuities when blurring.
      set depthCutoffBasedOnNormal(value) {
        if (scope.perspectiveBlur.params.depthCutoffBasedOnNormal === value) {
          return;
        }
        scope.perspectiveBlur.params.depthCutoffBasedOnNormal = value;
        scope.orthographicBlur.params.depthCutoffBasedOnNormal = value;
        scope.perspectiveBlurNormalMap.params.depthCutoffBasedOnNormal = value;
        scope.orthographicBlurNormalMap.params.depthCutoffBasedOnNormal = value;
      },
      get depthCutoffBasedOnNormal(): boolean {
        return scope.perspectiveBlur.params.depthCutoffBasedOnNormal;
      },

      set useNormalTextureInGTAO(value) {
        if (value === scope.useNormalTextureInGTAO) {
          return;
        }
        scope.useNormalTextureInGTAO = value;
        scope.refreshGTAOShaders();
      },
      get useNormalTextureInGTAO() {
        return scope.useNormalTextureInGTAO;
      },

      set cosineWeighting(value) {
        if (value === scope.cosineWeighting) {
          return;
        }
        scope.cosineWeighting = value;
        scope.refreshGTAOShaders();
      },
      get cosineWeighting() {
        return scope.cosineWeighting;
      },

      set usePoissonBlur(value) {
        scope.usePoissonBlur = value;
      },
      get usePoissonBlur() {
        return scope.usePoissonBlur;
      },

      // This value toggles whether normals are inferred from depth or if they're sampled from the normal texture
      // in the blur step.
      set useNormalTextureInBlur(value) {
        scope.useNormalTextureInBlur = value;
      },
      get useNormalTextureInBlur() {
        return scope.useNormalTextureInBlur;
      },

      set output(value) {
        if (value === scope.output) {
          return;
        }
        scope.output = value;
        scope.refreshGTAOShaders();
      },
      get output() {
        return scope.output as any;
      },
    };

    const initMaterialSet = function (isPerspectiveCamera) {
      const gtaoMaterial = new THREE.ShaderMaterial({
        defines: Object.assign({}, GTAOShader.defines),
        fragmentShader: GTAOShader.fragmentShader,
        vertexShader: GTAOShader.vertexShader,
        uniforms: THREE.UniformsUtils.clone(GTAOShader.uniforms),
        blending: THREE.NoBlending,
        depthTest: false,
        depthWrite: false,
        name: "GTAOPass gtaoMaterial",
      });

      if (!isPerspectiveCamera) {
        gtaoMaterial.defines["PERSPECTIVE_CAMERA"] = 0;
      }

      return {
        gtaoMaterial,
      };
    };

    this.perspectiveMaterials = initMaterialSet(true);
    this.orthographicMaterials = initMaterialSet(false);
    this.refreshGTAOShaders();
  }

  get screenSpaceAoMap(): THREE.Texture {
    return this.params.ssaoBlur ? this.blurFinalRenderTarget.texture : this.aoRenderTarget.texture;
  }

  render(renderer: THREE.WebGLRenderer, writeBuffer: THREE.WebGLRenderTarget | null) {
    this.clearSettingsSaver.saveClearSettings(renderer);
    renderer.autoClear = false;

    this.renderGTAO(renderer);
    this.blurAO(renderer, this.params);

    this.renderOutput(renderer, this.params.output, writeBuffer);

    this.clearSettingsSaver.restoreClearSettings(renderer);
  }

  private renderGTAO(renderer: THREE.WebGLRenderer) {
    const isPerspectiveCamera = (this.camera as THREE.PerspectiveCamera).isPerspectiveCamera;
    const materialCollection = isPerspectiveCamera ? this.perspectiveMaterials : this.orthographicMaterials;
    const gtaoMaterial = materialCollection.gtaoMaterial;

    screenSpaceViewPositionPrepareShader(gtaoMaterial, this.camera, this.depthNormalPass, {
      skipSettingDepthBuffer: true,
    });

    const depthTextures = [
      this.depthNormalPass.params.enableViewZConversion
        ? this.depthNormalPass.viewZTexture
        : this.depthNormalPass.depthTexture,
    ];
    let downsampledDepthIndex = DEPTH_MIP_STRIDE - 1;
    const depthMips = this.depthMipsForGTAOShader();
    while (depthTextures.length < depthMips) {
      depthTextures.push(this.depthNormalPass.getDepthMip(downsampledDepthIndex));
      downsampledDepthIndex += DEPTH_MIP_STRIDE;
    }
    gtaoMaterial.uniforms["tDepth"].value = depthTextures;

    gtaoMaterial.uniforms.gtaoWorldSpaceScale.value = this.params.worldSpaceScale;
    gtaoMaterial.uniforms["intensity"].value = this.params.aoIntensity;
    gtaoMaterial.uniforms["lowerIntensityOnVerticalSurfaces"].value = this.params.lowerIntensityOnVerticalSurfaces;

    this.camera.getWorldQuaternion(tempQuaternion);
    tempQuaternion.normalize();
    tempQuaternion.invert();
    gtaoMaterial.uniforms["viewWorldUp"].value.copy(this.worldUpVector);
    gtaoMaterial.uniforms["viewWorldUp"].value.applyQuaternion(tempQuaternion);

    gtaoMaterial.uniforms["maxScreenSpaceKernelRadius"].value = this.params.maxScreenSpaceKernelRadius;
    gtaoMaterial.uniforms.gtaoFalloffMinRelativeRadius.value = this.params.falloffMinRelativeRadius;
    gtaoMaterial.uniforms.gtaoFalloffMaxRelativeRadius.value = this.params.falloffMaxRelativeRadius;

    gtaoMaterial.uniforms["randomSeed"].value = this.randomSeed;

    // Rendering GTAO texture
    let ssaoRenderTarget = this.aoRenderTarget;
    if (this.output === SSFXRendererOutput.NORMAL_FOR_AO) {
      if (!this.aoDebugRenderTarget) {
        this.aoDebugRenderTarget = new THREE.WebGLRenderTarget(this.aoRenderResolution.x, this.aoRenderResolution.y, {
          minFilter: THREE.LinearFilter,
          magFilter: THREE.LinearFilter,
          format: THREE.RGBAFormat,
          depthBuffer: false,
          stencilBuffer: false,
        });
      }
      ssaoRenderTarget = this.aoDebugRenderTarget;
    }
    this.renderPass(renderer, gtaoMaterial, ssaoRenderTarget, 0xffffff, 1.0);
  }

  private depthMipsForGTAOShader() {
    return Math.floor(this.depthNormalPass.params.downsampleDepthSteps / DEPTH_MIP_STRIDE) + 1;
  }

  private refreshGTAOShader(materialSet) {
    const material = materialSet.gtaoMaterial;
    material.needsUpdate = true;
    material.defines["NORMAL_TEXTURE"] = this.useNormalTextureInGTAO ? 1 : 0;
    screenSpaceViewNormalPrepareShader(material, this.depthNormalPass);

    const writeNormalsFromShader = this.params.output === SSFXRendererOutput.NORMAL_FOR_AO;
    material.defines["OUTPUT_NORMALS"] = writeNormalsFromShader ? 1 : 0;

    material.defines["DEPTH_MIPS"] = this.depthMipsForGTAOShader();

    material.defines["AO_COSINE_WEIGHTING"] = this.cosineWeighting ? 1 : 0;

    // Set uniforms that don't need to be set on every frame.
    material.uniforms["depthInvSize"].value.set(1.0 / this.paddedSize.x, 1.0 / this.paddedSize.y);
    material.uniforms["targetInvSize"].value.set(1.0 / this.aoRenderResolution.x, 1.0 / this.aoRenderResolution.y);
  }

  private refreshGTAOShaders() {
    this.refreshGTAOShader(this.perspectiveMaterials);
    this.refreshGTAOShader(this.orthographicMaterials);
  }

  needsNormalTexture() {
    // TODO: Maybe remove useNormalTextureInGTAO and return true from here - probably GTAO won't work well without a normal texture.
    return this.useNormalTextureInGTAO || this.useNormalTextureInBlur || this.usePoissonBlur;
  }

  private refreshResolutionInMaterialSet(materialSet) {
    materialSet.gtaoMaterial.uniforms["depthInvSize"].value.set(1.0 / this.paddedSize.x, 1.0 / this.paddedSize.y);
    materialSet.gtaoMaterial.uniforms["targetInvSize"].value.set(
      1.0 / this.aoRenderResolution.x,
      1.0 / this.aoRenderResolution.y,
    );
  }

  protected refreshResolution() {
    super.refreshResolution();
    this.refreshResolutionInMaterialSet(this.perspectiveMaterials);
    this.refreshResolutionInMaterialSet(this.orthographicMaterials);
  }

  dispose() {
    super.dispose();
    const materialSets = [this.perspectiveMaterials, this.orthographicMaterials];
    for (let i = 0; i < 2; ++i) {
      materialSets[i].gtaoMaterial.dispose();
    }
  }
}
